import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import GameService from '../services/GameService';

const CategoryPage = () => {
  const { category } = useParams();
  const navigate = useNavigate();
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Category configuration
  const categoryConfig = {
    'sportsbook': {
      title: 'Sportsbook',
      description: 'Bet on your favorite sports',
      providers: ['SABASports(IBC)', 'DpSports', 'SBO', 'LuckSport'],
      icon: '🏈'
    },
    'casino': {
      title: 'Casino Games',
      description: 'Classic and modern casino games',
      providers: ['PragmaticPlay-Asia', 'PragmaticPlay-EU', 'PGSoft', 'JDBGaming', 'CQ9', 'Spribe'],
      icon: '🎰'
    },
    'evolution': {
      title: 'Evolution Live',
      description: 'Live casino games with real dealers',
      providers: ['Evolution Live', 'Evolution Live(Asia)', 'Evolution-BigTimeGaming', 'Evolution-NetEnt'],
      icon: '🎲'
    },
    'royal-gaming': {
      title: 'Royal Gaming',
      description: 'Premium gaming experience',
      providers: ['Royal Gaming'],
      icon: '👑'
    },
    'e-cricket': {
      title: 'E-Cricket',
      description: 'Virtual cricket betting',
      providers: ['VirtualSports'],
      icon: '🏏'
    },
    'card-games': {
      title: 'Card Games',
      description: 'Traditional Indian card games',
      providers: ['Card Games'],
      icon: '🃏'
    },
    'slot-games': {
      title: 'Slot Games',
      description: 'Exciting slot machine games',
      providers: ['PragmaticPlay-Asia', 'PGSoft', 'JDBGaming', 'GameArt', 'PlaynGo'],
      icon: '🎰'
    },
    'fishing-games': {
      title: 'Fishing Games',
      description: 'Interactive fishing games',
      providers: ['Fishing Games'],
      icon: '🎣'
    }
  };

  const currentCategory = categoryConfig[category];

  useEffect(() => {
    if (currentCategory) {
      fetchCategoryGames();
    } else {
      setError('Category not found');
      setLoading(false);
    }
  }, [category]);

  const fetchCategoryGames = async () => {
    try {
      setLoading(true);
      setError(null);

      // For now, we'll use mock data based on the category
      // In a real implementation, this would fetch from an API
      const mockGames = generateMockGamesForCategory(category);
      setGames(mockGames);
    } catch (err) {
      setError('Failed to load games');
      console.error('Error fetching games:', err);
    } finally {
      setLoading(false);
    }
  };

  const generateMockGamesForCategory = (categoryName) => {
    // Comprehensive game data based on CSV analysis
    const gameTemplates = {
      'sportsbook': [
        { name: 'SABA Sports', provider: 'SABA', type: 'Sports Game', uid: '08ced9dd788aed11ff3c7f387ae0f063' },
        { name: 'SBO Sportsbook', provider: 'SBO', type: 'Sports Game', uid: '07baf9e1388d32cd4cee0c0c91b23020' },
        { name: 'DPSports Gaming', provider: 'DP', type: 'Sports Game', uid: '23c2dca76f87d7b7f239833060c8751e' },
        { name: '568win Sportsbook', provider: 'SBO', type: 'Sports Game', uid: '171ffc7c5df076a4a4aedf892cd43212' },
        { name: 'LuckSport', provider: 'LuckSport', type: 'Sports Game', uid: 'luck-sport-uid' }
      ],
      'casino': [
        { name: 'Gates of Olympus', provider: 'Pragmatic', type: 'Video Slots', uid: '880a68222d05a3697055d523d574cb2b' },
        { name: 'Sweet Bonanza', provider: 'Pragmatic', type: 'Video Slots', uid: 'e3f45e2f03388056aba660b7e737ecf0' },
        { name: 'Mahjong Ways', provider: 'PGSoft', type: 'Slot Game', uid: '1189baca156e1bbbecc3b26651a63565' },
        { name: 'Fortune Tiger', provider: 'PGSoft', type: 'Slot Game', uid: 'c8b9643a5626bbb3ab614fc6d55090c1' },
        { name: 'Dragon Tiger', provider: 'Casino', type: 'Card Game', uid: '9904a181e77d690b94b7426dfa2311ca' },
        { name: 'Baccarat', provider: 'Casino', type: 'Card Game', uid: '7cbb72b1bbb091239a5b3ddd6709d804' },
        { name: 'Roulette', provider: 'Casino', type: 'Table Game', uid: 'ab22f33340fac5c424ba87c259204002' }
      ],
      'evolution': [
        { name: 'French Roulette Gold', provider: 'Evolution', type: 'CasinoLive', uid: '36b1e71c6f51827e24261d06a22b1e31' },
        { name: 'Speed Baccarat', provider: 'Evolution', type: 'CasinoLive', uid: 'e7931b2e0d410ddc4f88520e9ad87c4b' },
        { name: 'Blackjack VIP', provider: 'Evolution', type: 'CasinoLive', uid: '87a7f4550407f5ed73c3353a54a11187' },
        { name: 'Emperor Speed Baccarat', provider: 'Evolution', type: 'CasinoLive', uid: '7b44393101abad7ac31e21fc1bdb3d56' },
        { name: 'Speed Roulette', provider: 'Evolution', type: 'CasinoLive', uid: 'b4af506243cafae52908e8fa266f8ff6' },
        { name: 'Infinite Blackjack', provider: 'Evolution', type: 'CasinoLive', uid: '58d7089aa20bce7f70e0e2ce81e888f4' }
      ],
      'slot-games': [
        { name: 'Gates of Olympus', provider: 'Pragmatic', type: 'Video Slots', uid: '880a68222d05a3697055d523d574cb2b' },
        { name: 'Sweet Bonanza', provider: 'Pragmatic', type: 'Video Slots', uid: 'e3f45e2f03388056aba660b7e737ecf0' },
        { name: 'Mahjong Ways', provider: 'PGSoft', type: 'Slot Game', uid: '1189baca156e1bbbecc3b26651a63565' },
        { name: 'Treasures of Aztec', provider: 'PGSoft', type: 'Slot Game', uid: '2fa9a84d096d6ff0bab53f81b79876c8' },
        { name: 'Fortune Tiger', provider: 'PGSoft', type: 'Slot Game', uid: 'c8b9643a5626bbb3ab614fc6d55090c1' },
        { name: 'Fortune Rabbit', provider: 'PGSoft', type: 'Slot Game', uid: '5e679c5f6ca439829f4956d57baa9cd7' },
        { name: 'Starlight Princess', provider: 'Pragmatic', type: 'Video Slots', uid: '09d08939279289a03b89f2f146a7f817' },
        { name: 'Fortune Mouse', provider: 'PGSoft', type: 'Slot Game', uid: '65294436702e4366f583d31fc81483f2' }
      ],
      'fishing-games': [
        { name: 'Royal Fishing', provider: 'Fishing', type: 'Fishing Game', uid: 'e794bf5717aca371152df192341fe68b' },
        { name: 'Bombing Fishing', provider: 'Fishing', type: 'Fishing Game', uid: 'e333695bcff28acdbecc641ae6ee2b23' },
        { name: 'Dinosaur Tycoon', provider: 'Fishing', type: 'Fishing Game', uid: 'eef3e28f0e3e7b72cbca61e7924d00f1' },
        { name: 'Jackpot Fishing', provider: 'Fishing', type: 'Fishing Game', uid: '3cf4a85cb6dcf4d8836c982c359cd72d' },
        { name: 'Dragon Fortune', provider: 'Fishing', type: 'Fishing Game', uid: '1200b82493e4788d038849bca884d773' },
        { name: 'Mega Fishing', provider: 'Fishing', type: 'Fishing Game', uid: 'caacafe3f64a6279e10a378ede09ff38' }
      ],
      'card-games': [
        { name: 'Dragon Tiger', provider: 'Card Games', type: 'Card Game', uid: '9904a181e77d690b94b7426dfa2311ca' },
        { name: 'Baccarat', provider: 'Card Games', type: 'Card Game', uid: '7cbb72b1bbb091239a5b3ddd6709d804' },
        { name: 'Teen Patti', provider: 'Card Games', type: 'Card Game', uid: '7fd7e7fa1f41a74dc4dc2542afa0a788' },
        { name: 'Andar Bahar', provider: 'Card Games', type: 'Card Game', uid: '34eb3c1bff2b30c2540bd34c6f8af119' },
        { name: 'Point Rummy', provider: 'Card Games', type: 'Card Game', uid: '80ecd2441d840f74cbb0fb33056c1aaa' },
        { name: 'Call Break', provider: 'Card Games', type: 'Card Game', uid: '09bb57ef1cbdaa36dc575c66b4a545be' }
      ],
      'e-cricket': [
        { name: 'SBO VirtualSports', provider: 'SBO', type: 'VirtualSports', uid: '341827d4370bb198b18364e2d75e6916' },
        { name: 'Virtual Cricket', provider: 'Virtual', type: 'VirtualSports', uid: 'virtual-cricket-uid' },
        { name: 'E-Cricket Match', provider: 'Virtual', type: 'VirtualSports', uid: 'e-cricket-match-uid' }
      ],
      'royal-gaming': [
        { name: 'Royal Gaming Casino', provider: 'Royal Gaming', type: 'Casino', uid: 'royal-gaming-uid' },
        { name: 'Royal Slots', provider: 'Royal Gaming', type: 'Slot Game', uid: 'royal-slots-uid' },
        { name: 'Royal Live', provider: 'Royal Gaming', type: 'Live Casino', uid: 'royal-live-uid' }
      ]
    };

    return gameTemplates[categoryName] || [];
  };

  const handleGameClick = async (game) => {
    try {
      // Check if user is logged in
      const user = localStorage.getItem('reddyannabook_user');
      if (!user) {
        alert('Please login to play games.');
        navigate('/login');
        return;
      }

      // Parse user data
      const userData = JSON.parse(user);
      console.log('🎮 Launching game:', game);

      // Generate slug from game name
      const gameSlug = game.name
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '-')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '')
        .trim();

      const response = await GameService.launchGame(gameSlug, userData);
      if (response.success && response.data.game_url) {
        navigate('/game', {
          state: {
            gameUrl: response.data.game_url,
            gameTitle: game.name
          }
        });
      } else {
        throw new Error('Failed to launch game');
      }
    } catch (error) {
      console.error('Game launch error:', error);
      alert(`Failed to launch ${game.name}. Please try again.`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading games...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button 
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  if (!currentCategory) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">Category not found</p>
          <button 
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => navigate('/')}
                className="text-gray-600 hover:text-gray-900"
              >
                ← Back
              </button>
              <div className="flex items-center space-x-3">
                <span className="text-3xl">{currentCategory.icon}</span>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">{currentCategory.title}</h1>
                  <p className="text-gray-600">{currentCategory.description}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Games Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {games.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 text-6xl mb-4">{currentCategory.icon}</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No games available</h3>
            <p className="text-gray-600">Games for this category are coming soon!</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
            {games.map((game, index) => (
              <div
                key={index}
                onClick={() => handleGameClick(game)}
                className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer overflow-hidden"
              >
                <div className="aspect-square bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                  <span className="text-white text-2xl font-bold">
                    {game.name.charAt(0)}
                  </span>
                </div>
                <div className="p-3">
                  <h3 className="font-semibold text-sm text-gray-900 truncate">{game.name}</h3>
                  <p className="text-xs text-gray-600 truncate">{game.provider}</p>
                  <p className="text-xs text-gray-500 truncate">{game.type}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoryPage;
